import { useMemo, useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import styled from 'styled-components';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Zoom from '@mui/material/Zoom';
import Fade from '@mui/material/Fade';
import { Card } from '@buoyhealth/common.buoy-card';

import { Link } from '@buoyhealth/common.buoy-button';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';

import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { trackStructuredEvent } from 'src/analytics';
import useCompletion from './hooks/useCompletion';
import usePolarisActor from 'src/hooks/usePolarisActor';
import useInterview from '../../hooks/useInterview';
import { HelpfulQuestion } from '@buoy-components/user-feedback';
import { getCategory } from '../../analytics';
import LineLoader from '../LineLoader';
import TypewriterResults from '../TypewriterLoadingMessage';
import useChatList from './hooks/useChatList';
import useIdentifiers from '../../hooks/useIdentifiers';
import { TextButton } from '@buoyhealth/common.buoy-button';
import useTransition from '../../hooks/useTransition';
import AffiliateCard from '../AffiliateCard';
import useInterviewType from '../../hooks/useInterviewType';
import { trackEditClick, trackLoadingScreenImpression, trackCustomLinkClick } from './analytics';
import useIsPolaris from '../../hooks/useIsPolaris';
import CollapsibleResults from '../CollapsibleResults';
import RichEndings from '../Completion/components/RichEndings';
import ErrorContainer from '../ErrorContainer';

const AnswerContainer = styled.div`
  ${({ theme }) => theme.typography.variants.primary.main};
  background-color: ${({ theme }) => theme.palette.teal[0]};
  padding: 16px 16px 24px;
  border-radius: 8px;
`;

export const AnswerChip = styled.div`
  ${({ theme }) => theme.typography.variants.primary.main};
  font-size: 16px;
  border-radius: 30px;
  background-color: ${({ theme }) => theme.palette.gray[0]};
  border: 1px solid ${({ theme }) => theme.palette.status.success};
  line-height: 1;
  padding: 16px 24px;
`;

const Disclosure = styled(Grid)`
  p {
    margin-bottom: 0;
    ${({ theme }) => theme.typography.variants.secondary.small};
  }

  padding-bottom: 16px;
`;

const Completion = ({ triggerRestart }: { triggerRestart: () => void }) => {
  const [typewriterStopped, setTypewriterStopped] = useState(false);

  const state = usePolarisActor()?.getSnapshot();
  const completion = useCompletion();
  const chatList = useChatList();
  const interview = useInterview();
  const interviewType = useInterviewType();
  const identifiers = useIdentifiers();
  const theme = useTheme();
  const transition = useTransition();
  const isPolaris = useIsPolaris();

  const onEditClick = () => {
    trackEditClick(interview.token, interviewType);
    transition({
      event: 'EDIT_ANSWERS_CLICKED',
    });
  };

  const onCustomLinkClick = () => {
    trackCustomLinkClick(interview.token, interviewType);
  };

  const isShowingTreatmentGuidance = state?.matches('displayingTreatmentGuidance');

  const isLoading =
    state?.matches('fetchingTreatmentGuidance') || state?.matches('loadingInitializationData');

  const { link, results } = completion;
  const slug = state?.context.slug || '';

  useEffect(() => {
    trackLoadingScreenImpression(interview.token, interviewType);
  }, []);

  const summarizedAnswers = useMemo(() => {
    const answers = [] as any;
    if (identifiers.age.value) {
      answers.push(`${identifiers.age.value} years old`);
    }
    if (identifiers.sex) {
      answers.push(identifiers.sex === 'm' ? 'Male' : 'Female');
    }
    chatList.items?.forEach((item) => {
      if (typeof item.answer === 'string' && !!item.answer) {
        answers.push(item.answer);
      } else if (Array.isArray(item.answer)) {
        item.answer.forEach((answer) => {
          answers.push(answer.text);
        });
      }
    });
    return answers;
  }, [chatList]);

  const showDisclaimer = isPolaris ? true : !isShowingTreatmentGuidance;

  return (
    <>
      {isLoading && (
        <>
          <Grid item className="bp-shrink-0">
            <LineLoader />
          </Grid>
          <Grid item className="bp-shrink-0">
            <TypewriterResults
              onTypewriterStop={() => setTypewriterStopped(true)}
              loadingMessage="Analyzing your answers to find the best match."
            />
          </Grid>
          {!!summarizedAnswers.length && (
            <Grid item className="bp-shrink-0">
              <Fade in={typewriterStopped} style={{ transitionDelay: '200ms' }}>
                <AnswerContainer>
                  <Box display="flex" justifyContent="space-between">
                    <span>You told us:</span>
                    <Box display="flex" alignItems="center">
                      <EditOutlinedIcon
                        fontSize="small"
                        color="primary"
                        sx={{ marginRight: '6px' }}
                      />
                      <TextButton
                        onClick={onEditClick}
                        sx={{
                          textDecoration: 'underline',
                          fontSize: '14px !important',
                        }}
                      >
                        Edit answers
                      </TextButton>
                    </Box>
                  </Box>
                  <Grid container spacing={1} sx={{ paddingTop: '16px' }}>
                    {summarizedAnswers.map((answer: any, i: any) => (
                      <Grid key={answer} item>
                        <Zoom
                          in={typewriterStopped}
                          timeout={theme.transitions.duration.shortest}
                          style={{
                            transitionDelay: `${400 + 100 * (i + 1)}ms`,
                          }}
                        >
                          <AnswerChip key={answer}>{answer}</AnswerChip>
                        </Zoom>
                      </Grid>
                    ))}
                  </Grid>
                </AnswerContainer>
              </Fade>
            </Grid>
          )}
        </>
      )}
      <Grid item>
        <CollapsibleResults isShowingResults={!!isShowingTreatmentGuidance}>
          <Grid container direction="column" spacing={2}>
            {!isShowingTreatmentGuidance || results?.length ? (
              results?.map((result, i) => (
                <Grid item key={result.identifier}>
                  <AffiliateCard data={result} index={i} />
                </Grid>
              ))
            ) : (
              <Grid item>
                <ErrorContainer
                  headline="Hmm, we can't seem to find a good match for you."
                  triggerRestart={triggerRestart}
                />
              </Grid>
            )}
            {link && (
              <Grid item>
                <Card
                  backgroundColor={theme.palette.common.white}
                  elevation={2}
                  className="bp-mx-1 !bp-px-6 !bp-py-4"
                >
                  <div className="bp-flex">
                    <Link
                      href={link}
                      onClick={onCustomLinkClick}
                      target="_blank"
                      sx={{
                        textDecoration: 'none',
                      }}
                    >
                      <div className="bp-flex bp-w-full bp-items-center bp-justify-between bp-text-[0.875rem]">
                        <div>View more products on Amazon</div>
                        <div>
                          <OpenInNewOutlinedIcon
                            color="primary"
                            sx={{ marginLeft: '6px' }}
                            className="bp-text-[0.875rem]"
                          />
                        </div>
                      </div>
                    </Link>
                  </div>
                </Card>
              </Grid>
            )}
            <Grid item>
              <HelpfulQuestion
                snowplowProps={{
                  action: getCategory('treatmentGuidance'),
                  label: interview.token || '',
                }}
                trackingCallback={trackStructuredEvent}
                withBuoyTheme
              />
            </Grid>
            {showDisclaimer && (
              <Disclosure item>
                <p className="!bp-font-bold">Medical Disclosure:</p>
                <p>
                  This is not intended as medical advice. Any product use or safety concerns should
                  be reviewed with a healthcare professional.
                </p>
                <p className="mt-4 !bp-font-bold">Affiliate Disclosure:</p>
                <p>
                  The links provided may earn us a small commission at no additional cost to you if
                  you choose to purchase the recommended product. This support allows our research
                  and editorial team to continue providing high-quality recommendations. As a
                  participant in the Amazon Services LLC Associates Program, an affiliate
                  advertising initiative, we are able to earn advertising fees through providing
                  links to products on Amazon.com. Please rest assured that we only recommend
                  high-quality products.
                </p>
              </Disclosure>
            )}
          </Grid>
          {!!results?.length && (
            <RichEndings interviewType="treatmentGuidance" products={results} query={slug} />
          )}
        </CollapsibleResults>
      </Grid>
    </>
  );
};

export default Completion;
